/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: pages\dashboards\hooks\useDashboardLayouts.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Tools module.
────────────────────────────────────────────────────────────────*/


import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../../lib/api';
import { useThemeStore } from '../../../store/theme';

interface GridItem {
    x: number;
    y: number;
    w: number;
    h: number;
}

interface DashboardWidget {
    id: string;
    widgetType: string;
    title: string;
    config: Record<string, any>;
    gridItem: GridItem;
    isVisible: boolean;
    displayOrder: number;
}

interface DashboardLayout {
    id: string;
    name: string;
    description?: string;
    isDefault: boolean;
    widgets: DashboardWidget[];
    createdAt: string;
}

interface DashboardConfig {
    layouts: DashboardLayout[];
    activeLayoutId: string | null;
    gridColumns: number;
    gridRowHeight: number;
}

interface WidgetType {
    typeKey: string;
    name: string;
    description: string;
    category: string;
    icon: string;
    defaultConfig: Record<string, any>;
    defaultSize: { w: number; h: number; minW: number; minH: number };
    realTime: boolean;
}

// Constant empty array to prevent new array references on each render
const EMPTY_LAYOUTS: DashboardLayout[] = [];

// ─────────────────────────────────────────────────────────────────────────────
// 📐 Pixel-Perfect Dashboard Grid Standard (Prop Authority)
// - 24 columns for granular horizontal positioning
// - 20px row height for stable high-precision vertical sizing
// ─────────────────────────────────────────────────────────────────────────────
const GRID_COLUMNS = 48;
const GRID_ROW_HEIGHT = 10;

// Legacy grid used 24 columns - migration disabled as it cannot reliably detect legacy layouts
// const LEGACY_GRID_COLUMNS = 24;

/**
 * Migration function disabled - was incorrectly detecting new layouts as legacy.
 * If you have legacy 24-column layouts, they will need to be manually adjusted.
 */
function migrateLayoutTo48Columns(layout: DashboardLayout): DashboardLayout {
    // Migration disabled - return layout unchanged
    return layout;
}

export function useDashboardLayouts() {
    const [config, setConfig] = useState<DashboardConfig | null>(null);
    const [currentLayout, setCurrentLayout] = useState<DashboardLayout | null>(null);
    const [widgetTypes, setWidgetTypes] = useState<WidgetType[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Ref for currentLayout to allow stable callbacks
    const currentLayoutRef = useRef(currentLayout);
    useEffect(() => {
        currentLayoutRef.current = currentLayout;
    }, [currentLayout]);



    // Load dashboard config
    const loadConfig = useCallback(async () => {
        try {
            setLoading(true);

            const response = await api.get('/api/admin/dashboard');



            if (response.data.success) {
                const dashConfig = response.data.data;

                setConfig(dashConfig);

                // Set current layout
                if (dashConfig.layouts.length > 0) {
                    const activeLayout = dashConfig.layouts.find((l: DashboardLayout) => l.id === dashConfig.activeLayoutId)
                        || dashConfig.layouts.find((l: DashboardLayout) => l.isDefault)
                        || dashConfig.layouts[0];

                    // Migrate legacy 24-column layouts to 48-column grid
                    const migratedLayout = migrateLayoutTo48Columns(activeLayout);
                    setCurrentLayout(migratedLayout);
                } else {

                }
            }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            console.error('[useDashboardLayouts] loadConfig - ERROR:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Load widget types
    const loadWidgetTypes = useCallback(async () => {
        try {

            const response = await api.get('/api/admin/dashboard/widget-types');

            if (response.data.success) {
                setWidgetTypes(response.data.data);

            }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            console.error('[useDashboardLayouts] Failed to load widget types:', err);
        }
    }, []);

    // Create new layout
    const createLayout = useCallback(async (name: string, description?: string) => {
        try {
            setSaving(true);
            const response = await api.post('/api/admin/dashboard/layouts', {
                name,
                description,
                isDefault: false,
            });

            if (response.data.success) {
                await loadConfig();
                return response.data.data;
            }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }, [loadConfig]);

    // Delete layout
    const deleteLayout = useCallback(async (layoutId: string) => {
        try {
            setSaving(true);
            await api.delete(`/api/admin/dashboard/layouts/${layoutId}`);
            await loadConfig();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }, [loadConfig]);

    // Set active layout
    const setActiveLayout = useCallback(async (layoutId: string) => {
        try {
            await api.put('/api/admin/dashboard/active-layout', { layoutId });

            if (config) {
                const layout = config.layouts.find(l => l.id === layoutId);
                if (layout) {
                    // Migrate legacy 24-column layouts to 48-column grid
                    const migratedLayout = migrateLayoutTo48Columns(layout);
                    setCurrentLayout(migratedLayout);
                    setConfig({ ...config, activeLayoutId: layoutId });
                }
            }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            setError(err.message);
        }
    }, [config]);

    // Add widget
    const addWidget = useCallback(async (widgetType: string, title?: string) => {

        if (!currentLayout) {
            console.error('[useDashboardLayouts] addWidget - NO currentLayout! Cannot add widget.');
            return;
        }

        try {
            setSaving(true);

            const response = await api.post(`/api/admin/dashboard/layouts/${currentLayout.id}/widgets`, {
                widgetType,
                title,
            });



            if (response.data.success) {
                // Update local state
                const newWidget = response.data.data;

                setCurrentLayout({
                    ...currentLayout,
                    widgets: [...currentLayout.widgets, newWidget],
                });

                // Also update config
                if (config) {
                    setConfig({
                        ...config,
                        layouts: config.layouts.map(l =>
                            l.id === currentLayout.id
                                ? { ...l, widgets: [...l.widgets, newWidget] }
                                : l
                        ),
                    });
                }

                return newWidget;
            } else {
                console.error('[useDashboardLayouts] API returned success=false:', response.data);
            }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            console.error('[useDashboardLayouts] API Error:', err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }, [currentLayout, config]);

    // Update widget
    const updateWidget = useCallback(async (
        widgetId: string,
        updates: { title?: string; config?: Record<string, any>; gridItem?: GridItem; isVisible?: boolean }
    ) => {
        if (!currentLayout) return;

        try {
            await api.put(`/api/admin/dashboard/layouts/${currentLayout.id}/widgets/${widgetId}`, updates);

            // Update local state
            setCurrentLayout({
                ...currentLayout,
                widgets: currentLayout.widgets.map(w =>
                    w.id === widgetId ? { ...w, ...updates } : w
                ),
            });

            // Also update config
            if (config) {
                setConfig({
                    ...config,
                    layouts: config.layouts.map(l =>
                        l.id === currentLayout.id
                            ? {
                                ...l,
                                widgets: l.widgets.map(w =>
                                    w.id === widgetId ? { ...w, ...updates } : w
                                ),
                            }
                            : l
                    ),
                });
            }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            setError(err.message);
        }
    }, [currentLayout, config]);

    // Delete widget
    const deleteWidget = useCallback(async (widgetId: string) => {
        if (!currentLayout) return;

        try {
            await api.delete(`/api/admin/dashboard/layouts/${currentLayout.id}/widgets/${widgetId}`);

            // Update local state
            setCurrentLayout({
                ...currentLayout,
                widgets: currentLayout.widgets.filter(w => w.id !== widgetId),
            });

            // Also update config
            if (config) {
                setConfig({
                    ...config,
                    layouts: config.layouts.map(l =>
                        l.id === currentLayout.id
                            ? { ...l, widgets: l.widgets.filter(w => w.id !== widgetId) }
                            : l
                    ),
                });
            }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            setError(err.message);
        }
    }, [currentLayout, config]);

    // Update layout positions (batch update for drag-and-drop)
    const updateLayoutPositions = useCallback((
        newPositions: Array<{ id: string; x: number; y: number; w: number; h: number }>
    ) => {
        setCurrentLayout(prevLayout => {
            if (!prevLayout) return null;

            // Strict Check: Filter out any positions that haven't actually changed
            const actualChanges = newPositions.filter(pos => {
                const widget = prevLayout.widgets.find(w => w.id === pos.id);
                if (!widget || !widget.gridItem) return true;

                return (
                    widget.gridItem.x !== pos.x ||
                    widget.gridItem.y !== pos.y ||
                    widget.gridItem.w !== pos.w ||
                    widget.gridItem.h !== pos.h
                );
            });

            if (actualChanges.length === 0) {
                return prevLayout;
            }

            // Update local state immediately
            const updatedWidgets = prevLayout.widgets.map(widget => {
                const pos = newPositions.find(p => p.id === widget.id);
                if (pos) {
                    return {
                        ...widget,
                        gridItem: { x: pos.x, y: pos.y, w: pos.w, h: pos.h },
                    };
                }
                return widget;
            });

            const newLayout = { ...prevLayout, widgets: updatedWidgets };

            // CRITICAL: Update ref synchronously so saveLayout has the latest value
            currentLayoutRef.current = newLayout;

            return newLayout;
        });
    }, []);

    // Save current layout (persist to DB)
    // Uses a callback pattern to get the most recent layout state
    const saveLayout = useCallback(async () => {
        // Get the most current layout from the ref (updated synchronously before save)
        const layout = currentLayoutRef.current;
        if (!layout) {
            return;
        }

        setSaving(true);
        try {
            // Save all widgets positions SEQUENTIALLY to avoid race conditions
            // (parallel saves cause each read to get stale data before others finish writing)
            for (const w of layout.widgets) {
                await api.put(`/api/admin/dashboard/layouts/${layout.id}/widgets/${w.id}`, {
                    gridItem: w.gridItem
                });
            }
            // Don't reload config after save - it resets state. Just keep current state.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            console.error('Failed to save layout:', err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }, []);

    // Get or create default layout
    const getOrCreateDefault = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/admin/dashboard/default');

            if (response.data.success) {
                await loadConfig();
                return response.data.data;
            }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [loadConfig]);

    // Initialize on mount - only run once
    useEffect(() => {
        loadConfig();
        loadWidgetTypes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty deps - callbacks are stable (useCallback with []), only run on mount

    // Memoize the return object to prevent unnecessary re-renders
    // This is critical because returning a new object on every render can cause cascading re-renders
    return useMemo(() => ({
        config,
        currentLayout,
        widgetTypes,
        loading,
        saving,
        error,
        layouts: config?.layouts || EMPTY_LAYOUTS,
        // Fixed grid settings (hardcoded)
        gridColumns: GRID_COLUMNS,
        gridRowHeight: GRID_ROW_HEIGHT,
        loadConfig,
        createLayout,
        deleteLayout,
        setActiveLayout,
        addWidget,
        updateWidget,
        deleteWidget,
        updateLayoutPositions,
        saveLayout,
        getOrCreateDefault,
    }), [
        config,
        currentLayout,
        widgetTypes,
        loading,
        saving,
        error,
        loadConfig,
        createLayout,
        deleteLayout,
        setActiveLayout,
        addWidget,
        updateWidget,
        deleteWidget,
        updateLayoutPositions,
        saveLayout,
        getOrCreateDefault,
    ]);
}
